import { useEffect, useRef, useState } from "react";
import { ANALYTICS_TIME_ZONE } from "../shared/analytics";
import type { TransferTrackingReport } from "../shared/transfer-analytics";
import type { CollectorStatus } from "../shared/analytics-collector";
import { ApiError, request } from "./api";

type TransferResponse = TransferTrackingReport & { collector: CollectorStatus };
type Props = {
  query: string;
  reload: number;
  onAuthLost: () => void;
};
const PAGE_SIZE = 50;
const safeId = /^[A-Za-z0-9_-]{1,100}$/;
const stamp = (value: number | null) =>
  value === null
    ? "Not yet"
    : new Intl.DateTimeFormat("en-US", {
        timeZone: ANALYTICS_TIME_ZONE,
        dateStyle: "medium",
        timeStyle: "short",
      }).format(value);
const contactUrl = (locationId: string, contactId: string) =>
  safeId.test(locationId) && safeId.test(contactId)
    ? `https://app.gohighlevel.com/v2/location/${locationId}/contacts/detail/${contactId}`
    : null;

export function TransferAnalytics({ query, reload, onAuthLost }: Props) {
  const [offset, setOffset] = useState(0);
  const [report, setReport] = useState<TransferResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const lastScope = useRef<string | null>(null);

  useEffect(() => {
    setOffset(0);
    setReport(null);
  }, [query]);

  useEffect(() => {
    let current = true;
    let inFlight = false;
    let timer: number | undefined;
    setLoading(true);
    setError(null);
    const scope = `${query}:${offset}`;
    if (lastScope.current !== scope) setReport(null);
    lastScope.current = scope;
    const load = async () => {
      if (inFlight) return;
      inFlight = true;
      let delay = 30_000;
      try {
        const params = new URLSearchParams(query);
        if (offset) params.set("transferOffset", String(offset));
        const next = await request<TransferResponse>(
          "GET",
          `/api/admin/analytics/transfers?${params}`,
        );
        if (!current) return;
        setReport(next);
        setError(null);
        if (next.collector.state === "running") delay = 10_000;
      } catch (cause) {
        if (!current) return;
        if (cause instanceof ApiError && [401, 403].includes(cause.status)) {
          setReport(null);
          setError(null);
          onAuthLost();
          current = false;
        } else {
          setError(
            cause instanceof Error
              ? cause.message
              : "Could not load transfer evidence.",
          );
        }
      } finally {
        inFlight = false;
        if (current) {
          setLoading(false);
          if (document.visibilityState !== "hidden")
            timer = window.setTimeout(() => void load(), delay);
        }
      }
    };
    const onVisibility = () => {
      window.clearTimeout(timer);
      if (document.visibilityState === "visible") void load();
    };
    document.addEventListener("visibilitychange", onVisibility);
    if (document.visibilityState !== "hidden") void load();
    return () => {
      current = false;
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearTimeout(timer);
    };
  }, [query, reload, offset, onAuthLost]);

  const coverage = report?.coverage;
  const latestShown =
    report?.signals.items.reduce<number | null>(
      (latest, signal) =>
        Math.max(latest ?? signal.occurredAt, signal.occurredAt),
      null,
    ) ?? null;
  return (
    <section
      className="analytics-panel analytics-transfer"
      aria-labelledby="analytics-transfers-heading"
    >
      <div className="analytics-panel-heading">
        <div>
          <p className="eyebrow">REPORTED HANDOFFS</p>
          <h2 id="analytics-transfers-heading">Reported transfers</h2>
        </div>
      </div>
      <p className="analytics-transfer-description">
        Contacts with calls in the selected scope. Stage entries and staff
        reports are separate signals and may describe the same handoff. Phone
        connection and completion are unverified. Automatic checks cover
        contacts with calls in the past 72 hours. Older reports use saved
        history.
      </p>
      {error && (
        <p className="analytics-error" role="alert">
          {error}
        </p>
      )}
      {loading && !report && (
        <p className="analytics-loading" role="status">
          Loading transfer evidence…
        </p>
      )}
      {report && (
        <>
          <div
            className="analytics-transfer-summary"
            aria-label="Reported transfer counts"
          >
            <div>
              <span>Stage entries</span>
              <strong>{report.totals.stageEntries.toLocaleString()}</strong>
            </div>
            <div>
              <span>Staff reports</span>
              <strong>{report.totals.reportedHandoffs.toLocaleString()}</strong>
            </div>
            <div>
              <span>Contacts with signals</span>
              <strong>{report.totals.contacts.toLocaleString()}</strong>
            </div>
          </div>
          <div className="analytics-transfer-coverage" role="status">
            <strong>Cached contact signals</strong>
            <span>
              {coverage?.checkedContacts.toLocaleString() ?? 0} of{" "}
              {coverage?.eligibleContacts.toLocaleString() ?? 0} call linked
              contacts have cached checks in this scope.
            </span>
            <span>
              Latest contact check: {stamp(coverage?.syncedAt ?? null)} PT
            </span>
            {coverage?.coveredThroughAt !== null ? (
              <span>
                Contact signals collected through:{" "}
                {stamp(coverage?.coveredThroughAt ?? null)} PT
              </span>
            ) : (
              <span>Contact signal coverage is unverified.</span>
            )}
            <span>Latest signal shown: {stamp(latestShown)} PT</span>
            {(!coverage?.complete || coverage.coveredThroughAt === null) && (
              <span>
                Contact signal coverage is partial. More signals may be
                collected later.
              </span>
            )}
            {report.totals.stageEntries === 0 &&
              report.totals.reportedHandoffs === 0 &&
              (!coverage?.complete || coverage.coveredThroughAt === null) && (
                <span>
                  Zero cached signals does not establish that no handoff
                  occurred.
                </span>
              )}
          </div>
          {report.signals.items.length ? (
            <div className="analytics-table-scroll">
              <table>
                <caption className="sr-only">
                  Reported transfer evidence in Pacific time
                </caption>
                <thead>
                  <tr>
                    <th scope="col">When (PT)</th>
                    <th scope="col">Signal</th>
                    <th scope="col">Recorded by</th>
                    <th scope="col">Reported recipient</th>
                    <th scope="col">Contact</th>
                  </tr>
                </thead>
                <tbody>
                  {report.signals.items.map((signal) => {
                    const url = contactUrl(signal.locationId, signal.contactId);
                    return (
                      <tr key={`${signal.locationId}:${signal.id}`}>
                        <td>{stamp(signal.occurredAt)}</td>
                        <th scope="row">
                          {signal.kind === "stage"
                            ? "Stage entry"
                            : "Staff report"}
                        </th>
                        <td>
                          {signal.recordedByName ||
                            (signal.recordedByGhlUserId
                              ? "Outside selected scope"
                              : "Not recorded")}
                        </td>
                        <td>{signal.reportedRecipient || "Not recorded"}</td>
                        <td>
                          {url ? (
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Open contact
                            </a>
                          ) : (
                            "Unavailable"
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="analytics-empty">
              {coverage?.complete && coverage.coveredThroughAt !== null
                ? "No reported transfer signals in the collected scope."
                : "No reported transfer signals in cached results so far."}
            </p>
          )}
          <div className="analytics-pagination">
            <button
              type="button"
              className="secondary"
              disabled={offset === 0 || loading}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Previous signals
            </button>
            <span>
              {report.signals.items.length
                ? `Rows ${offset + 1}–${offset + report.signals.items.length}`
                : "No rows"}
            </span>
            <button
              type="button"
              className="secondary"
              disabled={report.signals.nextOffset === null || loading}
              onClick={() => setOffset(report.signals.nextOffset!)}
            >
              Next signals
            </button>
          </div>
        </>
      )}
    </section>
  );
}
